import React, { useState, useEffect, useRef } from 'react'
import { Modal, Button, Form, Accordion, Alert, Row, Col } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import Select, { components } from 'react-select'
import { stageWorksAPI, workStagesAPI } from '../../services/api'
import WorkItem from './WorkItem'
import AsrModal from './AsrModal'
import MaterialsModal from './MaterialsModal'

const CheckboxOption = (props) => (
  <components.Option {...props}>
    <input
      type="checkbox"
      checked={props.isSelected}
      onChange={() => null}
      style={{ marginRight: 8, pointerEvents: 'none' }}
    />
    {props.label}
  </components.Option>
)

const SearchDropdownIndicator = (props) => {
  const handleMouseDown = (event) => {
    if (props.selectProps.onSearchIconMouseDown) {
      props.selectProps.onSearchIconMouseDown(event)
    }
  }

  return (
    <components.DropdownIndicator {...props}>
      <span
        className="works-select-search-indicator"
        role="button"
        tabIndex={0}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            handleMouseDown(event)
          }
        }}
        aria-label="Поиск работ"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="currentColor" strokeWidth="2" />
        </svg>
      </span>
    </components.DropdownIndicator>
  )
}

const EditWorksModal = ({ show, onHide, workStage, project, currentUser }) => {
  const [works, setWorks] = useState([])
  const [availableWorks, setAvailableWorks] = useState([])
  const [searchableWorks, setSearchableWorks] = useState([])
  const [showAddForm, setShowAddForm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedWork, setSelectedWork] = useState(null)
  const [showAsrModal, setShowAsrModal] = useState(false)
  const [showMaterialsModal, setShowMaterialsModal] = useState(false)
  const [selectedWorksToAdd, setSelectedWorksToAdd] = useState([])
  const [foremanPercentage, setForemanPercentage] = useState('')
  const [foremanPercentageSaved, setForemanPercentageSaved] = useState(false)
  const [isMobileViewport, setIsMobileViewport] = useState(false)
  const [isMobileSearchEnabled, setIsMobileSearchEnabled] = useState(false)
  const [searchInputValue, setSearchInputValue] = useState('')
  const worksSelectRef = useRef(null)

  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  useEffect(() => {
    if (show && workStage) {
      setForemanPercentage(workStage.foreman_discount_percentage != null ? workStage.foreman_discount_percentage : '')
      setForemanPercentageSaved(false)
      loadWorks()
    }
  }, [show, workStage])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return

    const mediaQuery = window.matchMedia('(max-width: 767px)')
    const updateMobileState = () => setIsMobileViewport(mediaQuery.matches)
    updateMobileState()

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateMobileState)
      return () => mediaQuery.removeEventListener('change', updateMobileState)
    }

    mediaQuery.addListener(updateMobileState)
    return () => mediaQuery.removeListener(updateMobileState)
  }, [])

  const loadWorks = async () => {
    try {
      setLoading(true)
      const response = await stageWorksAPI.list(project.id, workStage.id)
      setWorks(response.data.works || [])
      setAvailableWorks(response.data.available_works || [])
      setSearchableWorks(response.data.searchable_works || [])
      setError(null)
    } catch (err) {
      setError('Ошибка загрузки работ')
      console.error('Error loading works:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleAddWork = async (e) => {
    e.preventDefault()

    if (selectedWorksToAdd.length === 0) {
      setError('Выберите хотя бы одну работу')
      return
    }

    try {
      setLoading(true)
      // Create each selected work with default values
      for (const selectedWork of selectedWorksToAdd) {
        await stageWorksAPI.create(project.id, workStage.id, {
          work_id: selectedWork.value,
          quantity: 1,
        })
      }
      await loadWorks()
      setSelectedWorksToAdd([])
      setShowAddForm(false)
      setError(null)
    } catch (err) {
      setError('Ошибка при добавлении работ')
      console.error('Error adding works:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateWork = async (workId, data) => {
    try {
      await stageWorksAPI.update(project.id, workStage.id, workId, data)
      await loadWorks()
    } catch (err) {
      setError('Ошибка при обновлении работы')
      console.error('Error updating work:', err)
    }
  }

  const handleToggleCompletion = async (workId) => {
    try {
      await stageWorksAPI.toggleCompletion(project.id, workStage.id, workId)
      await loadWorks()
    } catch (err) {
      setError('Ошибка при изменении статуса')
      console.error('Error toggling completion:', err)
    }
  }

  const handleDeleteWork = async (workId) => {
    if (!window.confirm('Удалить работу из этапа?')) {
      return
    }

    try {
      await stageWorksAPI.delete(project.id, workStage.id, workId)
      await loadWorks()
    } catch (err) {
      setError('Ошибка при удалении работы')
      console.error('Error deleting work:', err)
    }
  }

  const handleOpenAsr = (work) => {
    setSelectedWork(work)
    setShowAsrModal(true)
  }

  const handleOpenMaterials = (work) => {
    setSelectedWork(work)
    setShowMaterialsModal(true)
  }

  const handleAsrUpdated = async () => {
    setShowAsrModal(false)
    await loadWorks()
  }

  const handleMaterialsUpdated = async () => {
    setShowMaterialsModal(false)
    await loadWorks()
  }

  const handleSaveForemanPercentage = async () => {
    try {
      setLoading(true)
      const value = foremanPercentage === '' ? null : parseFloat(foremanPercentage)
      await workStagesAPI.update(project.id, workStage.id, {
        project_work_stage: { foreman_discount_percentage: value },
      })
      setForemanPercentageSaved(true)
      setError(null)
      setTimeout(() => setForemanPercentageSaved(false), 2000)
    } catch (err) {
      setError('Ошибка при сохранении процента мастера')
      console.error('Error saving foreman percentage:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setShowAddForm(false)
    setError(null)
    setSelectedWorksToAdd([])
    setForemanPercentage('')
    setForemanPercentageSaved(false)
    setIsMobileSearchEnabled(false)
    setSearchInputValue('')
    reset()
    onHide()
  }

  const isGlobalSearchMode = isMobileSearchEnabled || searchInputValue.trim().length > 0
  const worksOptionsSource = isGlobalSearchMode ? searchableWorks : availableWorks
  const worksOptions = worksOptionsSource.map((work) => ({
    value: work.id,
    label: isGlobalSearchMode && work.work_stage_name ? `${work.name} (${work.work_stage_name})` : work.name,
  }))
  const hasAddableWorks = searchableWorks.length > 0

  const handleSearchIconMouseDown = (event) => {
    if (!isMobileViewport) return

    event.stopPropagation()
    setIsMobileSearchEnabled(true)

    requestAnimationFrame(() => {
      worksSelectRef.current?.focus()
    })
  }

  if (!workStage) return null

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex flex-column">
              <span className="text-uppercase text-muted small fw-semibold">Этап работ</span>
              <h6 className="mb-0 fw-bold">{workStage.work_stage_name}</h6>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

          <div className="mb-4 p-3 bg-body-secondary rounded-3">
            <h6 className="mb-2 fw-semibold small text-muted text-uppercase">Процент мастера для этого этапа</h6>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={foremanPercentage}
                onChange={(e) => { setForemanPercentage(e.target.value); setForemanPercentageSaved(false) }}
                style={{ width: '110px' }}
                placeholder="0–100"
              />
              <span className="text-muted">%</span>
              <Button
                variant={foremanPercentageSaved ? 'success' : 'outline-primary'}
                size="sm"
                onClick={handleSaveForemanPercentage}
                disabled={loading}
              >
                {foremanPercentageSaved ? 'Сохранено' : 'Сохранить'}
              </Button>
            </div>
            <Form.Text className="text-muted">
              Процент от стоимости работы, который получает мастер за этот этап в данном проекте.
            </Form.Text>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-4">
            <div></div>
            {hasAddableWorks && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowAddForm(!showAddForm)}
              >
                {showAddForm ? 'Скрыть форму' : 'Добавить работы'}
              </Button>
            )}
          </div>

          {showAddForm && hasAddableWorks && (
            <div className="bg-body-secondary rounded-3 p-4 mb-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="mb-0 fw-semibold text-uppercase small text-muted">Добавление работ</h6>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setShowAddForm(false)}
                >
                  Скрыть
                </Button>
              </div>
              <Form onSubmit={handleAddWork}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold small text-muted mb-2">Выберите работы *</Form.Label>
                  <Select
                    ref={worksSelectRef}
                    isMulti
                    closeMenuOnSelect={false}
                    blurInputOnSelect={false}
                    hideSelectedOptions={false}
                    controlShouldRenderValue={false}
                    isSearchable={!isMobileViewport || isMobileSearchEnabled}
                    components={{ Option: CheckboxOption, DropdownIndicator: SearchDropdownIndicator }}
                    onSearchIconMouseDown={handleSearchIconMouseDown}
                    options={worksOptions}
                    value={selectedWorksToAdd}
                    onChange={setSelectedWorksToAdd}
                    onInputChange={(value, { action }) => {
                      if (action === 'input-change') {
                        setSearchInputValue(value)
                      }
                      if (action === 'menu-close') {
                        setSearchInputValue('')
                      }
                      return value
                    }}
                    onMenuClose={() => {
                      setSearchInputValue('')
                      if (isMobileViewport) {
                        setIsMobileSearchEnabled(false)
                      }
                    }}
                    placeholder={isMobileViewport ? 'Выберите работы' : 'Начните вводить название работы...'}
                    noOptionsMessage={() => (
                      isGlobalSearchMode
                        ? 'По всем этапам работы не найдены'
                        : 'Работы этого этапа не найдены'
                    )}
                    isDisabled={loading}
                    className="react-select-container works-multi-select"
                    classNamePrefix="react-select"
                  />
                  {isMobileViewport && (
                    <Form.Text className="text-muted d-block mt-1">
                      Без клавиатуры показываются работы этого этапа. По иконке лупы включается поиск по всем этапам.
                    </Form.Text>
                  )}
                  <Form.Text className="text-muted">
                    Вы можете выбрать несколько работ. Они будут добавлены с количеством 1. Количество и цену можно будет изменить при редактировании.
                  </Form.Text>
                </Form.Group>
                <div className="d-flex justify-content-end gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={() => setShowAddForm(false)}>
                    Отмена
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    type="submit"
                    disabled={loading || selectedWorksToAdd.length === 0}
                  >
                    {loading ? 'Добавление...' : 'Добавить работы'}
                  </Button>
                </div>
              </Form>
            </div>
          )}

          {availableWorks.length === 0 && works.length > 0 && (
            <p className="text-muted mb-3">Все работы этого этапа уже добавлены. Через поиск можно добавить работу из другого этапа.</p>
          )}

          {works.length > 0 ? (
            <div>
              <div className="d-flex align-items-center mb-3">
                <h6 className="mb-0 fw-semibold">Выбранные работы</h6>
                <span className="badge bg-primary bg-opacity-10 text-primary ms-2">{works.length}</span>
              </div>
              <Accordion>
                {works.map((work) => (
                  <WorkItem
                    key={work.id}
                    work={work}
                    project={project}
                    workStage={workStage}
                    currentUser={currentUser}
                    onUpdate={handleUpdateWork}
                    onToggleCompletion={handleToggleCompletion}
                    onDelete={handleDeleteWork}
                    onOpenAsr={handleOpenAsr}
                    onOpenMaterials={handleOpenMaterials}
                  />
                ))}
              </Accordion>
            </div>
          ) : (
            <p className="text-muted mb-0">Работы для этого этапа ещё не выбраны.</p>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-3">
          <Button variant="secondary" onClick={handleClose}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>

      {selectedWork && (
        <>
          <AsrModal
            show={showAsrModal}
            onHide={() => setShowAsrModal(false)}
            work={selectedWork}
            project={project}
            workStage={workStage}
            onUpdate={handleAsrUpdated}
          />
          <MaterialsModal
            show={showMaterialsModal}
            onHide={() => setShowMaterialsModal(false)}
            work={selectedWork}
            onUpdate={handleMaterialsUpdated}
          />
        </>
      )}
    </>
  )
}

export default EditWorksModal
